import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChevronLeft, FileText, Download, Clock, AlertCircle,
  UploadCloud, UserCircle, Send, Paperclip, Eye, Mail, Bot, User,
  MessageSquare, DollarSign, Archive
} from 'lucide-react';
import api from '../services/api.js';
import SolicitudDocViewer from '../components/SolicitudDocViewer.jsx';

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const ACTION_LABELS = {
  'Ingresado':                 { label: 'Solicitud Ingresada',         color: '#2563eb', bg: '#eff6ff', icon: <FileText size={14} /> },
  'Asignado':                  { label: 'Funcionario Asignado',        color: '#8b5cf6', bg: '#f5f3ff', icon: <UserCircle size={14} /> },
  'Cambio de Estado':          { label: 'Cambio de Estado',            color: '#7c3aed', bg: '#f5f3ff', icon: <Clock size={14} /> },
  'Documentos Agregados':      { label: 'Documentos Adjuntados',       color: '#059669', bg: '#ecfdf5', icon: <Paperclip size={14} /> },
  'Correo Enviado':            { label: 'Correo Enviado al Ciudadano', color: '#d97706', bg: '#fffbeb', icon: <Mail size={14} /> },
  'Respuesta del Ciudadano':   { label: 'Respuesta del Ciudadano',     color: '#0891b2', bg: '#ecfeff', icon: <User size={14} /> },
  'Mensaje del Ciudadano':     { label: 'Mensaje del Ciudadano',       color: '#0891b2', bg: '#ecfeff', icon: <MessageSquare size={14} /> },
  'Cotización Generada':       { label: 'Cotización Generada',         color: '#d4af37', bg: '#fefcf2', icon: <DollarSign size={14} /> },
  'Comprobante Subido':        { label: 'Comprobante de Pago Subido',  color: '#059669', bg: '#f0fdf4', icon: <DollarSign size={14} /> },
};

function getActionMeta(action) {
  return ACTION_LABELS[action] || { label: action, color: '#64748b', bg: '#f1f5f9', icon: <Bot size={14} /> };
}

/* ─── Modal Envío de Correo ────────────────────────────────────────────── */
function ModalCorreo({ solicitud, onClose, onSent, prefill }) {
  const [mensaje, setMensaje] = useState(prefill?.mensaje || '');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!mensaje.trim()) {
      toast.error('El mensaje es obligatorio.');
      return;
    }
    setSending(true);
    try {
      await api.post(`/archivero/solicitudes/${solicitud.id}/correo`, { mensaje });
      toast.success('Correo enviado y registrado en el historial.');
      onSent();
      onClose();
    } catch {
      toast.error('Error al enviar el correo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Send size={18} color="var(--primary)" /> Enviar Correo al Ciudadano
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Para: <strong>{solicitud.nombre_solicitante}</strong> ({solicitud.email})
          &nbsp;·&nbsp;Solicitud <strong>{solicitud.numero_seguimiento}</strong>
        </p>
        <div className="form-group">
          <label>Mensaje</label>
          <textarea rows={6} value={mensaje} onChange={e => setMensaje(e.target.value)}
            placeholder="Escribe aquí el mensaje para el ciudadano..." />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <><Send size={14} /> Enviar</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Modal Cotización ────────────────────────────────────────────── */
function ModalCotizacion({ solicitud, onClose, onSent }) {
  const [total, setTotal] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!total || isNaN(total) || total <= 0) {
      toast.error('El total debe ser un número válido mayor a 0.');
      return;
    }
    setSending(true);
    try {
      await api.post(`/archivero/solicitudes/${solicitud.id}/cotizacion`, { total, observaciones });
      toast.success('Cotización enviada al ciudadano.');
      onSent();
      onClose();
    } catch {
      toast.error('Error al generar la cotización.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarSign size={18} color="var(--primary)" /> Generar Cobro
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Se notificará por correo a <strong>{solicitud.nombre_solicitante}</strong>.
        </p>
        <div className="form-group">
          <label>Monto Total ($)</label>
          <input type="number" value={total} onChange={e => setTotal(e.target.value)} placeholder="Ej: 5000" />
        </div>
        <div className="form-group">
          <label>Observaciones o Detalle (Opcional)</label>
          <textarea rows={3} value={observaciones} onChange={e => setObservaciones(e.target.value)}
            placeholder="Ej: Copia de escritura + Copia con vigencia..." />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <><DollarSign size={14} /> Enviar Cobro</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────── */
export default function HistorialArchivero() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/gestion-archivero';

  const [solicitud, setSolicitud] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [estados, setEstados] = useState([]);

  // Documentos
  const [subirFiles, setSubirFiles]           = useState([]);
  const [subiendo, setSubiendo]               = useState(false);
  const [notificarCiudadano, setNotificarCiudadano] = useState(false);

  // Modales
  const [modalCorreo, setModalCorreo] = useState(false);
  const [modalCotizacion, setModalCotizacion] = useState(false);
  const [modalPrefill, setModalPrefill] = useState(null);

  useEffect(() => { 
    fetchEstados();
    fetchTodo(); 
  }, [id]);

  const fetchEstados = async () => {
    try {
      const { data } = await api.get('/estados?tipo=archivero');
      setEstados(data);
    } catch {
      toast.error('Error al cargar los estados');
    }
  };

  const fetchTodo = async () => {
    setLoading(true);
    try {
      const resSol = await api.get(`/archivero/solicitudes/${id}`);
      setSolicitud(resSol.data);
    } catch {
      toast.error('Error al cargar la información del archivero.');
      navigate(from);
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarEstado = async (estadoId) => {
    try {
      await api.patch(`/archivero/solicitudes/${solicitud.id}/estado`, { estado_id: estadoId });
      toast.success('Estado actualizado correctamente');
      fetchTodo();
    } catch {
      toast.error('Error al cambiar el estado');
    }
  };

  const handleAsignarme = async () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return;
    const user = JSON.parse(userStr);
    try {
      await api.patch(`/archivero/solicitudes/${solicitud.id}/asignar`, { worker_id: user.id });
      toast.success('Te has asignado esta solicitud.');
      fetchTodo();
    } catch {
      toast.error('Error al asignar.');
    }
  };

  const getEstadoNombre = (id) => {
    const st = estados.find(e => e.id === parseInt(id, 10));
    return st ? st.nombre : id;
  };

  const handleSubirDocs = async () => {
    if (!subirFiles.length) { toast.error('Selecciona al menos un archivo.'); return; }
    const fd = new FormData();
    subirFiles.forEach(f => fd.append('documentos[]', f));
    fd.append('enviar_correo', notificarCiudadano ? '1' : '0');
    setSubiendo(true);
    try {
      await api.post(`/archivero/solicitudes/${solicitud.id}/documentos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documentos adjuntados correctamente.');
      setSubirFiles([]);
      setNotificarCiudadano(false);
      fetchTodo();
    } catch {
      toast.error('Error al subir documentos.');
    } finally {
      setSubiendo(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '5rem' }}>
        <span className="spinner" style={{ width: '40px', height: '40px' }} />
        <p>Cargando historial del archivero...</p>
      </div>
    );
  }

  if (!solicitud) return null;

  const isRechazado = solicitud.estado?.nombre === 'Rechazado';
  const assignedToMe = (() => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    const user = JSON.parse(userStr);
    return solicitud.worker_id === user.id;
  })();

  return (
    <div className="container" style={{ padding: '2rem clamp(16px,5vw,48px)', maxWidth: '1200px', margin: '0 auto' }}>

      <button className="btn btn-secondary btn-sm"
        onClick={() => navigate(from)}
        style={{ marginBottom: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
        <ChevronLeft size={16} /> Volver a Gestión Archivero
      </button>

      {/* Header */}
      <div className="glass-card" style={{ marginBottom: '2rem', padding: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.8rem', color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Archive size={28} />
              Archivero <span style={{ fontFamily: 'monospace', color: 'var(--copper)' }}>{solicitud.numero_seguimiento || 'Sin número'}</span>
            </h1>
            <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-muted)' }}>
              RUT: <strong>{solicitud.rut_solicitante}</strong> — {solicitud.nombre_solicitante}
              &nbsp;·&nbsp;
              {solicitud.email}
              {solicitud.telefono && <>&nbsp;·&nbsp;{solicitud.telefono}</>}
            </p>
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Tipo: <strong>{solicitud.tipo_solicitud === 'escritura' ? 'Copia de Escritura' : 'Protocolizado'}</strong>
              &nbsp;·&nbsp; Notaría: {solicitud.notaria} 
              &nbsp;·&nbsp; Repertorio/Protocolización: {solicitud.tipo_solicitud === 'escritura' ? solicitud.repertorio : solicitud.numero_protocolizacion}
              &nbsp;·&nbsp; Año: {solicitud.anio || solicitud.fecha}
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <span className={`badge`} style={{
              fontSize: '1rem', padding: '0.5rem 1rem',
              backgroundColor: isRechazado ? '#fef2f2' : 'var(--bg-surface)',
              color: isRechazado ? '#ef4444' : 'var(--text)',
              border: isRechazado ? '1px solid #fca5a5' : '1px solid var(--border-color)',
              borderRadius: 8,
            }}>
              {solicitud.estado?.nombre || 'Desconocido'}
            </span>
            
            {solicitud.worker ? (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <UserCircle size={14} /> Asignado a: <strong>{solicitud.worker.name}</strong>
                {!assignedToMe && (
                  <button className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', marginLeft: 8 }} onClick={handleAsignarme}>Asignarme</button>
                )}
              </span>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={handleAsignarme} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <UserCircle size={14} /> Asignarme esta solicitud
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '2rem' }}>

        {/* ── COLUMNA IZQUIERDA: Timeline de Historial ────────────────── */}
        <div>
          <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={20} style={{ color: 'var(--copper)' }} /> Historial de Comunicaciones
          </h3>

          <div style={{ position: 'relative', paddingLeft: '1.5rem', borderLeft: '2px solid rgba(var(--copper-rgb), 0.2)' }}>
            {solicitud.workflow_steps?.length > 0 ? (
              solicitud.workflow_steps.map((step) => {
                let meta = getActionMeta(step.action);
                const esCiudadano = ['Respuesta del Ciudadano', 'Mensaje del Ciudadano'].includes(step.action);
                
                return (
                  <div key={step.id} style={{ marginBottom: '1.5rem', position: 'relative' }}>
                    {/* Dot */}
                    <span style={{
                      position: 'absolute', left: '-29px', top: '4px',
                      background: 'var(--bg-body)', padding: '3px', borderRadius: '50%',
                    }}>
                      <div style={{
                        width: 10, height: 10,
                        background: meta.color,
                        borderRadius: '50%',
                      }} />
                    </span>

                    {/* Timestamp + author */}
                    <p style={{ margin: '0 0 4px 0', fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {new Date(step.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                      {step.worker
                        ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><UserCircle size={13} /> {step.worker.name}</span>
                        : esCiudadano && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><User size={13} /> Ciudadano</span>
                      }
                    </p>

                    {/* Card */}
                    <div style={{
                      background: meta.bg,
                      padding: '1rem',
                      borderRadius: '8px',
                      border: `1px solid ${meta.color}22`,
                    }}>
                      <strong style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        marginBottom: '0.5rem', color: meta.color, fontSize: '0.88rem',
                      }}>
                        {meta.icon} {meta.label}
                      </strong>

                      {/* Mensaje o comentario */}
                      {step.comments && (
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                          {step.comments}
                        </p>
                      )}

                      {/* Cambio de estado */}
                      {step.action === 'Cambio de Estado' && step.old_state && step.new_state && (
                        <div style={{ fontSize: '0.82rem', color: '#374151', marginTop: step.comments ? '8px' : 0 }}>
                          Estado anterior <strong>{getEstadoNombre(step.old_state)}</strong>
                          {' '}→ Estado nuevo <strong>{getEstadoNombre(step.new_state)}</strong>
                        </div>
                      )}

                      {/* Botón de Respuesta (si es mensaje de ciudadano) */}
                      {esCiudadano && (
                        <div style={{ marginTop: '0.8rem', display: 'flex', justifyContent: 'flex-end' }}>
                          <button 
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: '0.75rem', padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                            onClick={() => {
                              setModalPrefill({ mensaje: `\n\n--- Mensaje original ---\n${step.comments || ''}` });
                              setModalCorreo(true);
                            }}
                          >
                            <Send size={12} /> Responder al Ciudadano
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-muted">Sin historial registrado aún.</p>
            )}
          </div>
        </div>

        {/* ── COLUMNA DERECHA: Acciones y Documentos ──────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>

          {/* Documento original ciudadano */}
          <div className="glass-card">
            <h4 style={{ margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Paperclip size={18} /> Documento Adjunto Ciudadano
            </h4>
            {!solicitud.s3_path ? (
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>El ciudadano no adjuntó documento.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ background: '#f0fdf4', padding: '0.7rem 0.9rem', borderRadius: 8, border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={16} color="var(--success)" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {solicitud.nombre_archivo_original}
                  </span>
                </div>
                <a href={`/api/v1/archivero/solicitudes/${solicitud.id}/documento`} target="_blank" rel="noreferrer"
                  className="btn btn-sm btn-secondary" style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, textDecoration: 'none' }}>
                  <Download size={14} /> Descargar Archivo
                </a>
              </div>
            )}
          </div>

          {/* Comprobante de Pago */}
          {solicitud.comprobante_pago_path && (
            <div className="glass-card">
              <h4 style={{ margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <DollarSign size={18} /> Comprobante de Pago
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ background: '#e0e7ff', padding: '0.7rem 0.9rem', borderRadius: 8, border: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={16} color="var(--primary)" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    Comprobante Ciudadano
                  </span>
                </div>
                <a href={`/api/v1/archivero/solicitudes/${solicitud.id}/documento?type=comprobante`} target="_blank" rel="noreferrer"
                  className="btn btn-secondary btn-sm" style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, textDecoration: 'none' }}>
                  <Download size={14} /> Descargar Comprobante
                </a>
              </div>
            </div>
          )}

          {/* Documentos adicionales del funcionario */}
          <div className="glass-card">
            <SolicitudDocViewer
              docs={solicitud.documentos || []}
              titulo="Documentos de Respuesta"
              emptyText="Sin documentos adicionales."
            />

            {/* Upload */}
            <div style={{ background: 'var(--bg-body)', padding: '0.85rem', borderRadius: 8, border: '1px dashed var(--border-color)' }}>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>Adjuntar documentos de respuesta al cliente:</p>
              <input type="file" multiple onChange={e => setSubirFiles(Array.from(e.target.files))}
                style={{ fontSize: '0.82rem', width: '100%', marginBottom: '0.6rem' }} />

              {/* Checkbox notificar */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '0.55rem 0.75rem',
                borderRadius: 8,
                border: `1.5px solid ${notificarCiudadano ? '#b87333' : 'var(--border-color)'}`,
                background: notificarCiudadano ? 'rgba(184,115,51,0.07)' : 'var(--bg-body)',
                cursor: 'pointer', userSelect: 'none',
                transition: 'all 0.18s ease',
                marginBottom: '0.2rem',
              }}>
                <input
                  type="checkbox"
                  checked={notificarCiudadano}
                  onChange={e => setNotificarCiudadano(e.target.checked)}
                  style={{ accentColor: '#b87333', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
                />
                <span style={{ fontSize: '0.82rem', lineHeight: 1.3 }}>
                  ✉️ <strong style={{ color: notificarCiudadano ? '#b87333' : 'inherit' }}>
                    Notificar al ciudadano
                  </strong>
                </span>
              </label>

              {subirFiles.length > 0 && (
                <>
                  <ul style={{ paddingLeft: '1.2rem', fontSize: '0.78rem', margin: '0 0 0.5rem', color: 'var(--text-muted)' }}>
                    {subirFiles.map((f, i) => <li key={i}>{f.name}</li>)}
                  </ul>
                  <button className="btn btn-primary btn-sm"
                    style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: 6 }}
                    onClick={handleSubirDocs} disabled={subiendo}>
                    <UploadCloud size={15} />
                    {subiendo ? 'Subiendo...' : `Subir ${subirFiles.length} archivo(s)${notificarCiudadano ? ' y notificar' : ''}`}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Gestión del Estado */}
          <div className="glass-card animate-fade-in" style={{ borderLeft: '4px solid var(--primary)' }}>
            <h4 style={{ margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={18} style={{ color: 'var(--primary)' }} /> Gestión de la Solicitud
            </h4>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.82rem', display: 'block', marginBottom: 4, fontWeight: 600 }}>
                Cambiar Estado:
              </label>
              <select className="form-control"
                value={solicitud.estado_id || solicitud.estado?.id || ''}
                onChange={e => handleCambiarEstado(e.target.value)}
                style={{ fontSize: '0.85rem', padding: '6px' }}>
                {estados.map(est => (
                  <option key={est.id} value={est.id}>{est.nombre}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button className="btn btn-secondary"
                style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, borderColor: '#d4af37', color: '#b87333', background: '#fefcf2' }}
                onClick={() => setModalCotizacion(true)}>
                <DollarSign size={16} /> Generar Cobro / Cotización
              </button>

              <button className="btn btn-primary"
                style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}
                onClick={() => setModalCorreo(true)}>
                <Send size={16} /> Enviar Correo al Ciudadano
              </button>
            </div>
          </div>

        </div>
      </div>

      {modalCorreo && (
        <ModalCorreo
          solicitud={solicitud}
          prefill={modalPrefill}
          onClose={() => { setModalCorreo(false); setModalPrefill(null); }}
          onSent={fetchTodo}
        />
      )}

      {modalCotizacion && (
        <ModalCotizacion
          solicitud={solicitud}
          onClose={() => setModalCotizacion(false)}
          onSent={fetchTodo}
        />
      )}
    </div>
  );
}
